import * as React from "react"
import { TextInput } from "components-design-system"
import PropTypes from "prop-types"
import EmptyFavorites from "../EmptyFavorites/EmptyFavorites"
import FavoriteItem from "../FavoriteItem/FavoriteItem"
import "./FavoritesTab.scss"

// api
import { useGetFavoriteStudies, useGetFavoriteRequests } from "api/hooks"
import { useHandleRequestFavorited } from "context/hooks/useAppState"

const FavoritesTab = ({ type = "studies", setIsOpen }) => {
  const { data: faveStudies, isLoading: studyFavesLoading } = useGetFavoriteStudies({ refetchOnMount: true })
  const [search, setSearch] = React.useState("")

  const {
    data: faveRequests,
    refetch: refetchFaveRequests,
    isLoading: requestFavesLoading,
  } = useGetFavoriteRequests({ refetchOnMount: true })

  const favorites = React.useMemo(() => {
    const getSortedAndFiltered = (favorites) => {
      const key = type === "studies" ? "study_id" : "request_id"
      return favorites
        ?.filter((study) => study[key].toLowerCase().includes(search.toLowerCase()))
        ?.sort((a, b) => a?.[key]?.localeCompare(b?.[key], "en", { numeric: true }))
    }
    if (type === "studies" && faveStudies) {
      return getSortedAndFiltered(faveStudies)
    } else if (type === "requests" && faveRequests) {
      return getSortedAndFiltered(faveRequests)
    }
    return []
  }, [type, faveStudies, faveRequests, search])

  // reset search when switching tabs
  React.useEffect(() => {
    setSearch("")
  }, [type])

  useHandleRequestFavorited(() => refetchFaveRequests())

  if (studyFavesLoading || requestFavesLoading) return null

  return (
    <div className="favorites-tab">
      {/* show text input if there are favorites to show or if no matching search results */}
      {(favorites?.length > 0 || search) && (
        <div className="favorite-input-container">
          <TextInput
            className="favorite-input"
            size="small"
            placeholder={`Search favorite ${type === "studies" ? "studies" : "requests"}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            suffix={
              <i
                type="button"
                className="fal fa-search favorite-icon"
                aria-label="toggle menu"
              />
            }
            allowClear
          />
        </div>
      )}

      {/* show favorites list if showing any search results */}
      {favorites?.length > 0 && (
        <div className="favorite-list">
          {favorites?.map((favorite) => (
            <FavoriteItem
              key={favorite.id}
              type={type}
              favorite={favorite}
              setIsOpen={setIsOpen}
            />
          ))}
        </div>
      )}

      {/* show empty state if no favorites to show */}
      {(favorites?.length === 0 || search) && (
        <EmptyFavorites
          type={type}
          isEmptySearch={!!search} // no user faves vs no search results
        />
      )}
    </div>
  )
}

FavoritesTab.propTypes = {
  type: PropTypes.oneOf(["studies", "requests"]),
  setIsOpen: PropTypes.func,
}

export default FavoritesTab
